import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { downloadCsv } from '../../utils/downloadCsv';
import { resolveVoucherRefs } from '../../api/voucherRefs';

export default function AccountLedger() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [account, setAccount] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchName, setBranchName] = useState('');
  const [voucherRefs, setVoucherRefs] = useState(new Map());
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(searchParams.get('from') || '');
  const [to, setTo] = useState(searchParams.get('to') || today);
  // Carried over from a branch-filtered drill-down (e.g. P&L) — not user-editable here,
  // just honored so the ledger shown matches what was actually clicked.
  const branch = searchParams.get('branch') || '';

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: acc }, jl] = await Promise.all([
      supabase.from('chart_of_accounts').select('*, account_types(name, category)').eq('id', id).single(),
      (async () => {
        // Supabase caps a single request at 1000 rows — a high-volume account (e.g. a main
        // bank account) can easily have more, so page through with .range() until exhausted.
        // .order('id') is added as a stable tiebreaker: entry_date + line_order alone aren't
        // guaranteed unique, and without a unique sort .range() paging can silently drop or
        // duplicate rows across the page boundary once data exceeds one page.
        const PAGE = 1000;
        let all = [], from_ = 0;
        while (true) {
          // .order(..., { referencedTable: 'journal_entries' }) does NOT reliably sort by
          // the joined table through this !inner select — confirmed live (a real ledger
          // came back with entry_date jumbled: 08-20, 08-21, 08-06, 08-13, 07-27...). Drop
          // it from the query (still order by line_order/id so paging itself stays stable)
          // and sort the fully-fetched array by entry_date in JS below instead.
          let q = supabase.from('journal_lines')
            .select('*, journal_entries!inner(id, entry_number, entry_date, description, status, reference_type, reference_id, branch_id)')
            .eq('account_id', id)
            .eq('journal_entries.status', 'POSTED')
            .lte('journal_entries.entry_date', to)
            .order('line_order', { ascending: true })
            .order('id', { ascending: true })
            .range(from_, from_ + PAGE - 1);
          if (from) q = q.gte('journal_entries.entry_date', from);
          if (branch) q = q.eq('journal_entries.branch_id', branch);
          const { data } = await q;
          if (!data?.length) break;
          all = all.concat(data);
          if (data.length < PAGE) break;
          from_ += PAGE;
        }
        all.sort((a, b) => {
          const da = a.journal_entries?.entry_date || '';
          const db = b.journal_entries?.entry_date || '';
          if (da !== db) return da < db ? -1 : 1;
          const lo = (a.line_order || 0) - (b.line_order || 0);
          if (lo !== 0) return lo;
          return String(a.id).localeCompare(String(b.id));
        });
        return all;
      })(),
    ]);
    setAccount(acc);
    setLines(jl || []);
    // JE numbers are internal — show the RV/PV behind each line where there is one.
    setVoucherRefs(await resolveVoucherRefs(jl || []));
    setLoading(false);
  }, [id, from, to, branch]);

  useEffect(() => {
    if (!branch) { setBranchName(''); return; }
    supabase.from('branches').select('name').eq('id', branch).single()
      .then(({ data }) => setBranchName(data?.name || ''));
  }, [branch]);

  // Same reference_type → drill-target mapping used by Supplier/Client Ledger: an
  // invoice's revenue leg AND its supplier-cost leg share the same reference_type/
  // reference_id, so INVOICE_TICKET/VISA/BOOKING always point straight at the invoice.
  const drillPath = (je) => {
    const refType = je?.reference_type;
    const refId = je?.reference_id;
    if      (refType === 'INVOICE_TICKET' || refType === 'INVOICE_VISA' || refType === 'INVOICE_BOOKING') return refId ? `/invoices/${refId}` : null;
    else if (refType === 'invoice'         && refId) return `/invoices/${refId}`;
    else if (refType === 'BILL'            && refId) return `/bills/${refId}`;
    else if (refType === 'TICKET_COST'     && refId) return `/tickets/${refId}`;
    else if (refType === 'VISA_COST'       && refId) return `/visas/${refId}`;
    else if (refType === 'BOOKING_COST'    && refId) return `/bookings/${refId}`;
    else if (refType === 'package'         && refId) return `/packages/${refId}`;
    else if (refType === 'PAYMENT_VOUCHER' && refId) return `/vouchers/payment/${refId}`;
    else if (['RECEIPT_VOUCHER', 'CHEQUE_DEPOSIT', 'CHEQUE_RETURN', 'RECEIPT_VOID'].includes(refType) && refId) return `/vouchers/receipt/${refId}`;
    else if (refType === 'DEPOSIT_APPLICATION' && refId) return `/invoices/${refId}`;
    else if (je?.id) return `/journals/${je.id}`;
    return null;
  };

  useEffect(() => { load(); }, [load]);

  let runningBalance = 0;
  const isDebitNormal = account?.account_types?.category === 'ASSET' || account?.account_types?.category === 'EXPENSE';

  const totalDR = lines.reduce((s, l) => s + parseFloat(l.debit_amount || 0), 0);
  const totalCR = lines.reduce((s, l) => s + parseFloat(l.credit_amount || 0), 0);
  const closingBalance = isDebitNormal ? totalDR - totalCR : totalCR - totalDR;

  const handleExport = () => {
    if (!lines.length) return;
    let bal = 0;
    const rows = [
      ['Date', 'Reference', 'Description', 'Debit', 'Credit', 'Balance'],
      ...lines.map(l => {
        const dr = parseFloat(l.debit_amount || 0);
        const cr = parseFloat(l.credit_amount || 0);
        bal += isDebitNormal ? (dr - cr) : (cr - dr);
        return [
          l.journal_entries?.entry_date?.slice(0, 10) || '',
          voucherRefs.get(l.journal_entries?.id) || l.journal_entries?.entry_number || '',
          l.description || l.journal_entries?.description || '',
          dr.toFixed(2), cr.toFixed(2), bal.toFixed(2),
        ];
      }),
    ];
    downloadCsv(rows, `${account?.account_code || 'account'}-ledger${branchName ? `-${branchName.replace(/\s+/g, '-')}` : ''}.csv`);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{account?.account_code} — {account?.account_name}</div>
          <div className="page-subtitle">{account?.account_types?.category}{branchName ? ` · ${branchName}` : ''}</div>
          {/* Only visible when printed — the interactive date inputs below are hidden then */}
          <div className="print-only" style={{ display: 'none', fontSize: 13, color: '#555', marginTop: 4 }}>
            {from ? `${from} to ${to}` : `Up to ${to}`}
          </div>
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div><label className="form-label">From</label><input className="form-input" type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
          <div><label className="form-label">To</label><input className="form-input" type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
          <button className="btn btn-secondary" onClick={handleExport}>Export Excel</button>
          <button className="btn btn-secondary" onClick={() => window.print()}>Print</button>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Total Debits</div><div className="stat-value amount">{totalDR.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Total Credits</div><div className="stat-value amount">{totalCR.toLocaleString()}</div></div>
        <div className="stat-card"><div className="stat-label">Closing Balance</div><div className="stat-value amount" style={{ color: closingBalance >= 0 ? 'var(--success)' : 'var(--danger)' }}>{closingBalance.toLocaleString()}</div></div>
      </div>

      <div className="card">
        {loading ? <div className="spinner" /> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Reference</th><th>Description</th>
                  <th style={{ textAlign: 'right' }}>Debit</th>
                  <th style={{ textAlign: 'right' }}>Credit</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => {
                  const dr = parseFloat(l.debit_amount || 0);
                  const cr = parseFloat(l.credit_amount || 0);
                  runningBalance += isDebitNormal ? (dr - cr) : (cr - dr);
                  const path = drillPath(l.journal_entries);
                  return (
                    <tr key={l.id} onClick={() => path && navigate(path)} style={{ cursor: path ? 'pointer' : 'default' }}>
                      <td>{l.journal_entries?.entry_date?.slice(0, 10)}</td>
                      <td style={{ fontFamily: 'monospace', color: 'var(--primary)' }} title={l.journal_entries?.entry_number}>{voucherRefs.get(l.journal_entries?.id) || l.journal_entries?.entry_number}</td>
                      <td>{l.description || l.journal_entries?.description}</td>
                      <td style={{ textAlign: 'right' }} className="amount">{dr > 0 ? dr.toLocaleString() : ''}</td>
                      <td style={{ textAlign: 'right' }} className="amount">{cr > 0 ? cr.toLocaleString() : ''}</td>
                      <td style={{ textAlign: 'right', fontWeight: 500 }} className="amount">{runningBalance.toLocaleString()}</td>
                    </tr>
                  );
                })}
                {lines.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>No transactions in this period</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
